export type FiringMode = 'manual' | 'auto';

export enum TrainingMode {
  Quick = 'quick',
}

// * IMPORTANT: when modifying this enum, make sure TDS updates their code accordingly for image extrtaction to continue working
export enum HardwareVersion {
  Arc2022 = 'Arc 2022',
  Arc2023 = 'Arc 2023',
  Arc2024 = 'Arc 2024',
  Arc2025 = 'Arc 2025',
  Arc2026 = 'Arc 2026',
  Arc2027 = 'Arc 2027',

  // refurbished versions
  Arc2022Refurbished = 'Arc 2022-Refurbished',
  Arc2023Refurbished = 'Arc 2023-Refurbished',
  Arc2024Refurbished = 'Arc 2024-Refurbished',
  Arc2025Refurbished = 'Arc 2025-Refurbished',
}

const VISION_DISABLED_HW_VERSIONS = [
  HardwareVersion.Arc2022,
  HardwareVersion.Arc2023,
  HardwareVersion.Arc2024,
];

export const VISION_ENABLED_HW_VERSIONS = Object.values(HardwareVersion).filter(
  (v) => !VISION_DISABLED_HW_VERSIONS.includes(v)
);

export enum BallType {
  CPBL = 'CPBL',
  ChinaL10Pro = 'L10 Pro - China',
  BadenDimpledSeams = 'Baden Dimpled w/ Seams',
  KBO = 'KBO',
  L10Pro = 'L10 Pro',
  Marv = 'MARV',
  MiLB = 'MiLB',
  MLB = 'MLB',
  NCAA = 'NCAA',
  NPB = 'NPB',
  Smash = 'Driveline Smashfactor',
  Smash_42 = 'Driveline Smashfactor 4.2oz',
  StingFreeJugs = 'Sting-Free Jugs', // ! deprecate
  Trajekt_36 = 'Trajekt 3.6oz',
  Trajekt_42 = 'Trajekt 4.2oz',
  Trajekt_50 = 'Trajekt 5.0oz',
  Misc = 'MISC',
}

export const FIVE_OZ_BALL_TYPES = [
  BallType.L10Pro,
  BallType.MLB,
  BallType.MiLB,
  BallType.NCAA,
  BallType.NPB,
  BallType.KBO,
  BallType.Trajekt_50,
  BallType.CPBL,
];

/** if the machine is using any ball type from this array,
 * rapid mode should be engaged automatically */
export const SEAMLESS_BALL_TYPES: BallType[] = [
  BallType.Smash,
  BallType.StingFreeJugs,
];

export enum FireOption {
  SkipConfidenceCheck = 'SkipConfidenceCheck',
}

export const MS_LIMITS = {
  /** ft, checked during pitch upload process and before sending mstarget to machine */
  RELEASE_FT: {
    MAX: 65,
    MIN: 45,
  },
  /** rpm */
  WHEEL_SPEED: {
    MIN: 100,
    MAX: 3800,
  },
  /** degrees */
  ALPHAS: {
    MIN: -24,
    MAX: 24,
  },
  /** degrees */
  TILT: {
    MIN: -5,
    MAX: 6,
  },
  /** degrees */
  YAW: {
    MIN: -8,
    MAX: 8,
  },
  /** ft */
  POSITION: {
    X: {
      MIN: -3.2,
      MAX: 3.2,
    },
    Z: {
      MIN: 4.1,
      MAX: 7,
    },
  },
};

export enum PassResult {
  Pass = 'pass',
  Mean = 'mean',
  Max = 'max',
  Both = 'both',
}

export enum InstallStepStatus {
  Incomplete = 'Incomplete',
  Complete = 'Complete',
  Skipped = 'Skipped',
}

export const ALLOCATED_STREAM_METADATA_BYTES = 4;

export enum MachineStatus {
  CALIBRATING = 'CALIBRATING',
  ERROR = 'ERROR',
  ESTOP_ENGAGED = 'ESTOP_ENGAGED',
  ESTOP_RECOVERY = 'ESTOP_RECOVERY',
  FIRING = 'FIRING',
  HEALTH_CHECK = 'HEALTH_CHECK', // ? machine is in the middle of a health check
  IDLE = 'IDLE', // allows s2m
  LOADING = 'LOADING', // allows s2m
  MULTI_BALL = 'MULTI_BALL',
  NEEDS_CALIBRATION = 'NEEDS_CALIBRATION', // ! deprecate after FW 3.6
  NEEDS_REALIGNMENT = 'NEEDS_REALIGNMENT', // new as of FW 3.6
  NO_BALL = 'NO_BALL',
  PROC_DIED = 'PROC_DIED',
  R2F = 'R2F', // allows s2m
  RELAY_RESTART = 'RELAY_RESTART',
  UPGRADING = 'UPGRADING',
  WRONG_TARGET = 'WRONG_TARGET',
  UNKNOWN = 'UNKNOWN', // ? not a real status but used to avoid undefined checks
}

export const MACHINE_STATUSES_THAT_ALLOW_AUTO_FIRE = [
  MachineStatus.FIRING,
  MachineStatus.LOADING,
  MachineStatus.R2F,
];

export enum StrikeZoneType {
  Default = 'Default',
  MlbAbs = 'MLB ABS',
  Stringer = 'Stringer',
}

export type MachinePerformanceMode = 'all' | 'bp' | 'training';

export enum CalibrateProc {
  All = 'all',
  Gantry = 'gantry',
  Alphas = 'alphas',
  Projector = 'projector',
  ProjectorMound = 'projector_mound',
  ProjectorOutline = 'projector_outline',
  ProjectorRulers = 'projector_rulers',
}

export enum BurnInTestType {
  Firing = 'firing',
  Range = 'range',
}
